import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import './OverviewTab.css';

/**
 * Merchants Tab Component
 * Displays Merchant table for FI Admins with Reports page styling
 * Shows per-merchant metrics within an FI
 */
export function MerchantsTab({ merchantMetrics, fiId }) {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US').format(value || 0);
  }

  function handleViewConfig(merchantId) {
    navigate(`/fis/${fiId}/merchants/${merchantId}/config`);
  }

  function handleViewTransactions(merchantId) {
    navigate(`/solventpay/transactions?fi_id=${fiId}&account_id=${merchantId}`);
  }

  const filteredMerchants = (merchantMetrics || []).filter(merchant => {
    if (!searchTerm) return true;
    const lowerSearch = searchTerm.toLowerCase();
    const merchantName = (merchant.business_name || '').toLowerCase();
    const merchantId = String(merchant.terra_account_id || '');
    return merchantName.includes(lowerSearch) || merchantId.includes(lowerSearch);
  });

  return (
    <div className="fi-table-section">
      <div className="fi-table-header">
        <h3 className="fi-table-title">Merchants</h3>
        <div className="fi-search-container">
          <FontAwesomeIcon icon={['fal', 'search']} className="fi-search-icon" />
          <input
            type="text"
            className="fi-search-input"
            placeholder="Search merchants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="fi-table-container">
        <table className="fi-table">
          <thead>
            <tr>
              <th className="col-name">Merchant Name</th>
              <th className="col-transactions text-right">Transactions</th>
              <th className="col-volume text-right">Volume</th>
              <th className="col-fees text-right">Fees Collected</th>
              <th className="col-commission text-right">Your Share</th>
              <th className="col-actions text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMerchants.length === 0 ? (
              <tr>
                <td colSpan={6} className="fi-table-empty">
                  <div className="fi-table-empty-icon">
                    <FontAwesomeIcon icon={['fal', 'store']} />
                  </div>
                  {searchTerm ? 'No merchants match your search' : 'No merchants found'}
                </td>
              </tr>
            ) : (
              filteredMerchants.map((merchant) => (
                <tr key={merchant.terra_account_id}>
                  <td className="col-name">
                    <span
                      className="fi-name-link"
                      onClick={() => handleViewConfig(merchant.terra_account_id)}
                    >
                      {merchant.business_name || `Merchant #${merchant.terra_account_id}`}
                    </span>
                    <div className="fi-id-caption">ID: {merchant.terra_account_id}</div>
                  </td>
                  <td className="col-transactions text-right">
                    {formatNumber(merchant.transaction_count)}
                  </td>
                  <td className="col-volume text-right">
                    {formatCurrency(merchant.total_volume_usd)}
                  </td>
                  <td className="col-fees text-right">
                    {formatCurrency(merchant.total_fees_collected)}
                  </td>
                  <td className="col-commission text-right">
                    {formatCurrency(merchant.commission_earned)}
                  </td>
                  <td className="col-actions text-center">
                    <button
                      className="action-button"
                      onClick={() => handleViewTransactions(merchant.terra_account_id)}
                    >
                      Transactions
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredMerchants.length > 0 && (
        <div className="fi-table-footer">
          <span>
            Showing {filteredMerchants.length} of {(merchantMetrics || []).length} Merchants
          </span>
        </div>
      )}
    </div>
  );
}
